import { type IText } from 'fabric';
import { useCanvasEditor, type SelectedTextStyle } from './CanvasEditorContext';
import { formatFontName, parseFontId } from '@/utils/fontLoader';
import { playObjectAnimation, type AnimatableObject } from './CanvasAnimations';
import ColorPickerInput from '../commos/ColorPickerInput';

const FONT_SIZE_STEP = 1;
const MIN_FONT_SIZE = 6;
const MAX_FONT_SIZE = 300;

type TextAlignment = 'left' | 'center' | 'right';

const ALIGN_OPTIONS: TextAlignment[] = ['left', 'center', 'right'];

const isTextObject = (object: unknown): object is IText =>
  !!object && typeof object === 'object' && 'fontFamily' in object;

// Text stores the font variant id ("NTTaDa-Bold"), so the label comes from the id itself;
// values that are not a variant id (e.g. a CSS stack) show their first name as is.
const getFontFamilyLabel = (fontFamily: string) => {
  const firstName = fontFamily.split(',')[0].trim();
  if (firstName !== fontFamily) return firstName;
  const { family, variant } = parseFontId(fontFamily);
  return variant ? `${formatFontName(family)} ${formatFontName(variant)}` : formatFontName(family);
};

const ALIGN_ITEMS_CLASS: Record<TextAlignment, string> = {
  left: 'items-start',
  center: 'items-center',
  right: 'items-end',
};

const AlignBars = ({ align }: { align: TextAlignment }) => {
  const itemsClass = ALIGN_ITEMS_CLASS[align];
  return (
    <span className={`flex flex-col gap-0.75 w-4 ${itemsClass}`}>
      <span className="h-0.5 w-full bg-current rounded-full" />
      <span className="h-0.5 w-2/3 bg-current rounded-full" />
      <span className="h-0.5 w-4/5 bg-current rounded-full" />
    </span>
  );
};

const ICON_BUTTON_CLASSNAME = 'w-7 h-7 flex items-center justify-center rounded cursor-pointer text-gray-600 hover:bg-gray-100';
const ICON_BUTTON_ACTIVE_CLASSNAME = 'w-7 h-7 flex items-center justify-center rounded cursor-pointer bg-gray-200 text-gray-900';
const TEXT_BUTTON_CLASSNAME =
  'px-3 h-7 flex items-center gap-1 shrink-0 whitespace-nowrap text-sm text-gray-700 rounded cursor-pointer hover:bg-gray-100';

// Contextual floating bar: appears centered above the canvas when an object is selected.
// For text it shows typography controls; for any other object (e.g. image) it only
// shows the 3 shared actions (Animate / View layers switch tabs; Preview plays the element's animation).
const CanvasFloatingToolbar = () => {
  const { canvas, selectedObject, applyTextStyle, setActiveRailTab } = useCanvasEditor();

  if (!selectedObject) return null;

  const textObject = isTextObject(selectedObject) ? selectedObject : null;
  const hasAnimation = !!(selectedObject as AnimatableObject).animationConfig?.type;

  const update = (style: SelectedTextStyle) => applyTextStyle(style);

  return (
    <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-1 px-2 py-1.5 bg-white rounded-lg shadow-xl border border-gray-200">
      {textObject && (
        <>
          <span
            style={{ fontFamily: textObject.fontFamily }}
            className="inline-block px-2 h-7 leading-7 shrink-0 text-sm font-medium text-gray-700 max-w-28 truncate"
            title={textObject.fontFamily}
          >
            {getFontFamilyLabel(textObject.fontFamily)}
          </span>

          <span className="w-px h-5 bg-gray-200 mx-1" />

          <button
            type="button"
            onClick={() => update({ fontSize: Math.max(MIN_FONT_SIZE, textObject.fontSize - FONT_SIZE_STEP) })}
            className={ICON_BUTTON_CLASSNAME}
          >
            −
          </button>
          <span className="w-8 text-center text-sm text-gray-700">{Math.round(textObject.fontSize)}</span>
          <button
            type="button"
            onClick={() => update({ fontSize: Math.min(MAX_FONT_SIZE, textObject.fontSize + FONT_SIZE_STEP) })}
            className={ICON_BUTTON_CLASSNAME}
          >
            +
          </button>

          <span className="w-px h-5 bg-gray-200 mx-1" />

          <button
            type="button"
            onClick={() => update({ fontWeight: Number(textObject.fontWeight) >= 700 ? 400 : 700 })}
            className={Number(textObject.fontWeight) >= 700 ? ICON_BUTTON_ACTIVE_CLASSNAME : ICON_BUTTON_CLASSNAME}
          >
            <span className="text-sm font-bold">B</span>
          </button>
          <button
            type="button"
            onClick={() => update({ fontStyle: textObject.fontStyle === 'italic' ? 'normal' : 'italic' })}
            className={textObject.fontStyle === 'italic' ? ICON_BUTTON_ACTIVE_CLASSNAME : ICON_BUTTON_CLASSNAME}
          >
            <span className="text-sm italic">I</span>
          </button>
          <button
            type="button"
            onClick={() => update({ underline: !textObject.underline })}
            className={textObject.underline ? ICON_BUTTON_ACTIVE_CLASSNAME : ICON_BUTTON_CLASSNAME}
          >
            <span className="text-sm underline">U</span>
          </button>

          <span className="w-px h-5 bg-gray-200 mx-1" />

          <ColorPickerInput
            value={typeof textObject.fill === 'string' ? textObject.fill : '#000000'}
            onChange={(color) => update({ fill: color })}
            className="w-7 h-7"
          />

          <span className="w-px h-5 bg-gray-200 mx-1" />

          {ALIGN_OPTIONS.map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => update({ textAlign: align })}
              className={textObject.textAlign === align ? ICON_BUTTON_ACTIVE_CLASSNAME : ICON_BUTTON_CLASSNAME}
            >
              <AlignBars align={align} />
            </button>
          ))}

          <span className="w-px h-5 bg-gray-200 mx-1" />
        </>
      )}

      <button type="button" onClick={() => setActiveRailTab('animacion')} className={TEXT_BUTTON_CLASSNAME}>
        Animar
      </button>
      <button type="button" onClick={() => setActiveRailTab('capas')} className={TEXT_BUTTON_CLASSNAME}>
        Ver capas
      </button>
      <button
        type="button"
        disabled={!hasAnimation}
        onClick={() => {
          if (canvas && hasAnimation) playObjectAnimation(canvas, selectedObject as AnimatableObject);
        }}
        className={`${TEXT_BUTTON_CLASSNAME} disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent`}
      >
        Vista previa
      </button>
    </div>
  );
};

export default CanvasFloatingToolbar;
